import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type ICategorySelector from './interfaces/ICategorySelector';

import styles from './scss/CategorySelector.module.scss';

const formatCategoryLabel = (label: string): string => {
  const normalizedLabel = label.trim();

  if (!normalizedLabel) {
    return '';
  }

  const [codePart, ...descriptionParts] = normalizedLabel.split(' - ');

  if (!descriptionParts.length) {
    return (
      normalizedLabel.charAt(0).toUpperCase()
      + normalizedLabel.slice(1).toLowerCase()
    );
  }

  const description = descriptionParts.join(' - ').trim();

  if (!description) {
    return codePart.trim();
  }

  return [
    codePart.trim(),
    `${description.charAt(0).toUpperCase()}${description.slice(1).toLowerCase()}`,
  ].join(' - ');
};

function CategorySelector({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  className,
  disabled,
  openByDefault = false,
  formatLabel = formatCategoryLabel,
}: ICategorySelector) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(openByDefault);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<CSSProperties>({});
  const [isOpenUpward, setIsOpenUpward] = useState(false);

  const INPUT_HEIGHT = 48;
  const OPTIONS_VERTICAL_GAP = 12;
  const OPTIONS_DEFAULT_MAX_HEIGHT = 280;

  const updateDropdownPosition = useCallback(() => {
    if (!rootRef.current) {
      return;
    }

    const rect = rootRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const availableSpaceAbove = rect.top;
    const availableSpaceBelow = viewportHeight - rect.bottom;
    const requiredSpace = INPUT_HEIGHT + OPTIONS_VERTICAL_GAP + OPTIONS_DEFAULT_MAX_HEIGHT;

    const nextIsOpenUpward = (
      availableSpaceBelow < requiredSpace
      && availableSpaceAbove > availableSpaceBelow
    );

    setIsOpenUpward(nextIsOpenUpward);

    setDropdownPosition({
      top: rect.top,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const handleCloseDropdown = useCallback(() => {
    setIsOpen(false);
    inputRef.current?.blur();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideTrigger = rootRef.current?.contains(target);
      const isClickInsideDropdown = dropdownRef.current?.contains(target);

      if (!isClickInsideTrigger && !isClickInsideDropdown) {
        handleCloseDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCloseDropdown, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updateDropdownPosition();

    const handleWindowResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('scroll', handleWindowResize, true);

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('scroll', handleWindowResize, true);
    };
  }, [isOpen, updateDropdownPosition]);

  const filteredOptions = useMemo(() => options.filter((option) => (
    (option.label || option.name || option.value)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  )), [options, searchTerm]);

  const selectedValue = formatLabel(value?.label || value?.name || value?.value || '');

  const handleToggleOpen = () => {
    if (disabled) {
      return;
    }

    setIsOpen((prev) => {
      if (prev) {
        inputRef.current?.blur();
      }

      return !prev;
    });
  };

  const handleSelectOption = (option: IOption) => {
    onChange(option);
    setSearchTerm('');
    handleCloseDropdown();
  };

  return (
    <div className={clsx(styles.root, className)} ref={rootRef}>
      <button
        type="button"
        className={clsx(styles.trigger, {
          [styles.triggerHidden]: isOpen,
        })}
        onClick={handleToggleOpen}
        disabled={disabled}
      >
        <span className={clsx(styles.triggerText, {
          [styles.placeholder]: !selectedValue,
        })}
        >
          {selectedValue || placeholder}
        </span>
      </button>
      {isOpen
        ? createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdownPortal}
            style={dropdownPosition}
          >
            <div className={clsx(styles.dropdown, {
              [styles.dropdownUp]: isOpenUpward,
            })}
            >
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={searchPlaceholder}
                className={styles.input}
              />
              <div className={styles.options}>
                {filteredOptions.map((option) => {
                  const optionLabel = option.label || option.name || option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={styles.option}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={() => handleSelectOption(option)}
                    >
                      <span className={styles.optionLabel}>
                        {formatLabel(optionLabel)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

export default CategorySelector;
